import { useCreate, useDelete } from "@refinedev/core";
import { List, useTable } from "@refinedev/antd";
import {
  Alert,
  Button,
  Divider,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Space,
  Tabs,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useState, type FC } from "react";
import type { CreatedToken, Token } from "../../types";

dayjs.extend(relativeTime);

const { Text } = Typography;

export const TokenList: FC = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"form" | "reveal">("form");
  const [createdToken, setCreatedToken] = useState<CreatedToken | null>(null);
  const [form] = Form.useForm();

  const { tableProps } = useTable<Token>({ syncWithLocation: false });
  const { mutate: createToken, mutation: { isPending: creating } } = useCreate<CreatedToken>();
  const { mutate: deleteToken } = useDelete();

  const handleClose = () => {
    setOpen(false);
    setStep("form");
    setCreatedToken(null);
    form.resetFields();
  };

  const handleSubmit = (values: { label: string }) => {
    createToken(
      {
        resource: "tokens",
        values: { label: values.label },
      },
      {
        onSuccess: (data) => {
          setCreatedToken(data.data as unknown as CreatedToken);
          setStep("reveal");
        },
      }
    );
  };

  return (
    <>
      <List
        title="Tokens"
        headerButtons={
          <Button type="primary" onClick={() => setOpen(true)}>
            New token
          </Button>
        }
        canCreate={false}
      >

        <Table
          {...tableProps}
          rowKey="id"
          locale={{
            emptyText: (
              <div style={{ padding: "32px 0" }}>
                <p style={{ fontWeight: 600, marginBottom: 4 }}>No tokens yet</p>
                <p style={{ color: "#888", margin: 0 }}>
                  Generate a token to authenticate the CLI in your CI pipeline. Only your Loopress token is needed, no WordPress credentials required.
                </p>
              </div>
            ),
          }}
        >
          <Table.Column<Token> dataIndex="label" title="Label" />
          <Table.Column<Token>
            dataIndex="createdAt"
            title="Created"
            render={(v: string) => dayjs(v).format("MMM D, YYYY")}
          />
          <Table.Column<Token>
            dataIndex="lastUsedAt"
            title="Last used"
            render={(v: string | null) =>
              v ? dayjs(v).fromNow() : <Text type="secondary">Never</Text>
            }
          />
          <Table.Column<Token>
            title="Actions"
            render={(_, record) => (
              <Popconfirm
                title="Delete token"
                description="This token will stop working immediately. Delete anyway?"
                onConfirm={() => deleteToken({ resource: "tokens", id: record.id })}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button danger size="small">
                  Delete
                </Button>
              </Popconfirm>
            )}
          />
        </Table>
      </List>

      <Drawer
        title={step === "form" ? "New token" : "Token generated"}
        open={open}
        onClose={() => {
          if (step !== "reveal") handleClose();
        }}
        closable={step !== "reveal"}
        maskClosable={step !== "reveal"}
        keyboard={step !== "reveal"}
        width={480}
        footer={
          step === "reveal" ? (
            <Button type="primary" block onClick={handleClose}>
              Done
            </Button>
          ) : null
        }
      >
        {step === "form" ? (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="label"
              label="Label"
              rules={[{ required: true, message: "Label is required" }]}
            >
              <Input placeholder="e.g. GitHub Actions - staging deploy, laptop" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={creating} block>
              Generate
            </Button>
          </Form>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Alert
              type="success"
              message="Copy your token now, it won't be shown again"
              showIcon
            />
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>YOUR TOKEN</Text>
              <div style={{ marginTop: 4 }}>
                <Text code copyable style={{ fontSize: 13, wordBreak: "break-all" }}>
                  {createdToken?.rawToken}
                </Text>
              </div>
            </div>
            <Divider style={{ margin: 0 }} />
            <div>
              <Text strong>Use in CI</Text>
              <p style={{ color: "#888", fontSize: 13, marginTop: 4, marginBottom: 12 }}>
                Add <Text code>LOOPRESS_TOKEN</Text> as a repository secret, then reference it in your workflow:
              </p>
              <Tabs
                size="small"
                items={[
                  {
                    key: "github",
                    label: "GitHub Actions",
                    children: (
                      <pre style={{ background: "#f6f8fa", border: "1px solid #e1e4e8", borderRadius: 6, padding: "12px 16px", fontSize: 12, margin: 0, overflowX: "auto" }}>
                        {`- name: Deploy with Loopress\n  run: npx @loopress/cli push\n  env:\n    LOOPRESS_TOKEN: \${{ secrets.LOOPRESS_TOKEN }}`}
                      </pre>
                    ),
                  },
                  {
                    key: "gitlab",
                    label: "GitLab CI",
                    children: (
                      <pre style={{ background: "#f6f8fa", border: "1px solid #e1e4e8", borderRadius: 6, padding: "12px 16px", fontSize: 12, margin: 0, overflowX: "auto" }}>
                        {`deploy:\n  script:\n    - npx @loopress/cli push\n  variables:\n    LOOPRESS_TOKEN: $LOOPRESS_TOKEN`}
                      </pre>
                    ),
                  },
                ]}
              />
            </div>
          </Space>
        )}
      </Drawer>
    </>
  );
};
